/**
 * T285 — Admin page (MVP): quartal-status close/reopen.
 *
 * Stbr/Admin can close/reopen; GF sees the same list read-only. Route is
 * accessible to everyone — backend enforces actual role via
 * `requireAuth + withTenant`; close/reopen produce audit entries.
 */
import { useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { ProtectedRoute } from '../components/ProtectedRoute';
import { AppShell } from '../components/layout/AppShell';
import { Button, LoadingSpinner, PageHeader, StatusBadge, TabNav } from '../components/ui';
import {
  useQuartals,
  useCloseQuartal,
  useReopenQuartal,
} from '../services/api/quartal';
import { useToastHelpers } from '../components/ui/Toast';
import { useSession } from '../hooks/useTenantContext';
import { AdminAuditLogViewer } from '../components/admin/AdminAuditLogViewer';
import { IntegrationsSection } from '../components/admin/IntegrationsSection';
import { StorageMigrationPanel } from '../components/admin/StorageMigrationPanel';
import { formatDateBerlin } from '../lib/format-date';

export const Route = createFileRoute('/admin')({
  component: AdminPage,
});

function AdminPage() {
  return (
    <ProtectedRoute>
      <AppShell>
        <AdminInner />
      </AppShell>
    </ProtectedRoute>
  );
}

type AdminTab = 'quartal' | 'integrations' | 'storage' | 'audit';

function AdminInner() {
  const year = new Date().getFullYear();
  const { data, isLoading } = useQuartals(year);
  const close = useCloseQuartal();
  const reopen = useReopenQuartal();
  const toast = useToastHelpers();
  const { data: session } = useSession();
  const canManage =
    session?.activeRole === 'admin' || session?.activeRole === 'steuerberater';
  const isAdmin = session?.activeRole === 'admin';
  // T459 (FR-043g): storage-migration is per-tenant data ops; the API
  // gate moved from `admin` to `geschäftsführer`. The UI tab visibility
  // must follow — otherwise admin sees the tab and 403s, while GF can't
  // reach the panel at all (codex review-3 P1).
  const isGeschaeftsfuehrer = session?.activeRole === 'geschäftsführer';
  const [tab, setTab] = useState<AdminTab>('quartal');

  return (
    <div className="space-y-6">
      <PageHeader title="Administration" subtitle="Quartals-Abschluss + Mandanten-Konfiguration" />

      {/* 006b P6: shared TabNav (aus genau diesem TabButton ratifiziert) */}
      <TabNav
        aria-label="Admin Bereiche"
        active={tab}
        onChange={(id) => setTab(id as typeof tab)}
        items={[
          { id: 'quartal', label: 'Quartals-Status', testid: 'admin-tab-quartal' },
          { id: 'integrations', label: 'Integrationen', testid: 'admin-tab-integrations' },
          ...(isGeschaeftsfuehrer
            ? [{ id: 'storage', label: 'Storage-Migration', testid: 'admin-tab-storage' }]
            : []),
          ...(isAdmin
            ? [{ id: 'audit', label: 'Admin-Audit-Log', testid: 'admin-tab-audit' }]
            : []),
        ]}
      />

      {tab === 'integrations' ? (
        <section>
          <h2 className="text-lg font-medium text-white mb-3">
            Integrationen (IMAP + Google Drive)
          </h2>
          <IntegrationsSection />
        </section>
      ) : tab === 'storage' && isGeschaeftsfuehrer ? (
        <section>
          <h2 className="text-lg font-medium text-white mb-3">
            Storage-Migration (Local → Remote SFTP)
          </h2>
          <StorageMigrationPanel />
        </section>
      ) : tab === 'audit' && isAdmin ? (
        <section>
          <h2 className="text-lg font-medium text-white mb-3">Admin-Audit-Log</h2>
          <AdminAuditLogViewer />
        </section>
      ) : (
        <QuartalSection
          year={year}
          data={data}
          isLoading={isLoading}
          canManage={canManage}
          onClose={async (quarter) => {
            try {
              await close.mutateAsync({ year, quarter });
              toast.success(`Q${quarter} abgeschlossen`);
            } catch (e) {
              toast.error((e as Error).message);
            }
          }}
          onReopen={async (quarter) => {
            try {
              await reopen.mutateAsync({ year, quarter });
              toast.success(`Q${quarter} wieder geöffnet`);
            } catch (e) {
              toast.error((e as Error).message);
            }
          }}
        />
      )}
    </div>
  );
}

// 006b P6: TabButton nach ui/TabNav extrahiert (PATTERNS.md §4).

interface QuartalSectionProps {
  year: number;
  data: ReturnType<typeof useQuartals>['data'];
  isLoading: boolean;
  canManage: boolean;
  onClose: (quarter: number) => void;
  onReopen: (quarter: number) => void;
}

function QuartalSection({
  year,
  data,
  isLoading,
  canManage,
  onClose,
  onReopen,
}: QuartalSectionProps) {
  return (
    <>
      <section>
        <h2 className="text-lg font-medium text-white mb-3">Quartals-Status {year}</h2>
        {isLoading && <LoadingSpinner />}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((q) => {
            const row = data?.items.find((r) => r.quarter === q);
            const status = row?.status ?? 'open';
            return (
              <div
                key={q}
                className={`p-4 rounded-lg border ${
                  status === 'closed'
                    ? 'bg-amber-500/10 border-amber-500/30'
                    : 'bg-white/5 border-white/10'
                }`}
              >
                <div className="text-sm text-white/50 mb-1">Q{q} / {year}</div>
                {/* 006b P5: Lifecycle-Zustand → StatusBadge (Kanon §8) */}
                <div className="mb-2">
                  <StatusBadge tone={status === 'closed' ? 'warning' : 'success'}>
                    {status === 'closed' ? 'Abgeschlossen' : 'Offen'}
                  </StatusBadge>
                </div>
                {row?.closedAt && (
                  <div className="text-xs text-white/40 mb-2">
                    Geschlossen {formatDateBerlin(row.closedAt)}
                  </div>
                )}
                {canManage && status === 'open' && (
                  <Button variant="ghost" onClick={() => onClose(q)}>
                    Schließen
                  </Button>
                )}
                {canManage && status === 'closed' && (
                  <Button variant="ghost" onClick={() => onReopen(q)}>
                    Wieder öffnen
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      </section>

      {!canManage && (
        <p className="text-xs text-white/40">
          Nur Steuerberater oder Administratoren können Quartale schließen.
        </p>
      )}
    </>
  );
}
